import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ result }) {
  // result = "success" or "cancel"
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, ok: false, credits: 0, email: "", status: "", reason: "" });
  const [testMode, setTestMode] = useState(false);
  const lang = localStorage.getItem("pl_lang") || "ar";
  const ar = lang === "ar";

  // Fetch test-mode flag once (used to gate the test-card hint)
  useEffect(() => {
    axios.get(`${API}/payments/packages`)
      .then(r => setTestMode(!!r.data.test_mode))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (result === "cancel") {
      setState({ loading: false, ok: false, credits: 0, email: "", status: "cancelled", reason: "" });
      return;
    }
    // Tap redirects with ?tap_id=chg_xxx (or sometimes without). We use it, or fall back to url params.
    const chargeId = params.get("tap_id") || params.get("charge_id");
    if (!chargeId) {
      setState({ loading: false, ok: false, credits: 0, email: "", status: "unknown", reason: "" });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${chargeId}`);
        if (data.payment_status === "paid") {
          if (data.email) localStorage.setItem("pl_email", data.email);
          if (!cancelled) setState({ loading: false, ok: true, credits: data.credits, email: data.email || "", status: "paid", reason: "" });
          return;
        }
        if (["failed", "declined", "cancelled", "abandoned", "expired"].includes(data.payment_status)) {
          if (!cancelled) setState({
            loading: false, ok: false, credits: 0,
            email: data.email || "",
            status: data.payment_status,
            reason: data.response_message || "",
          });
          return;
        }
      } catch {}
      if (tries < 20 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setState({ loading: false, ok: false, credits: 0, email: "", status: "timeout", reason: "" });
    };
    poll();
    return () => { cancelled = true; };
  }, [params, result]);

  return (
    <div
      dir={ar ? "rtl" : "ltr"}
      className="min-h-screen flex items-center justify-center px-4 bg-[#1f2a47] text-white"
    >
      <div className="max-w-md w-full text-center space-y-5 rounded-3xl p-8 border border-[#2e3a5c] bg-[#1f2a47]" data-testid="payment-result">
        {state.loading ? (
          <>
            <Loader2 className="h-14 w-14 mx-auto animate-spin text-[#18c98f]" />
            <h1 className={`text-2xl font-bold ${ar ? "font-arabic" : "font-outfit"}`}>
              {ar ? "جارٍ تأكيد الدفع..." : "Confirming payment..."}
            </h1>
            <p className="text-white/70 text-sm">
              {ar ? "قد يستغرق ذلك بضع ثوانٍ." : "This can take a few seconds."}
            </p>
          </>
        ) : state.ok ? (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#18c98f]" />
            <h1 className={`text-2xl font-bold ${ar ? "font-arabic" : "font-outfit"}`} data-testid="payment-success-title">
              {ar ? "تم الدفع بنجاح 🎉" : "Payment successful 🎉"}
            </h1>
            <div className="rounded-2xl bg-[#18c98f]/10 border border-[#18c98f]/40 p-4">
              <div className="text-[11px] uppercase tracking-widest text-[#18c98f]">
                {ar ? "تمّت إضافة" : "Credits added"}
              </div>
              <div className="text-4xl font-bold font-mono-num mt-1">
                {state.credits} <span className="text-lg text-[#18c98f]">{ar ? "اقتراح" : "credits"}</span>
              </div>
              {state.email && (
                <div className="text-xs text-white/60 mt-2" dir="ltr">
                  {state.email}
                </div>
              )}
            </div>
            <p className={`text-sm text-white/80 ${ar ? "font-arabic" : "font-outfit"}`}>
              {ar
                ? "اقتراحاتك جاهزة الآن ولن تنتهي صلاحيتها. استخدمها متى شئت."
                : "Your credits are ready and never expire. Use them anytime."}
            </p>
            <Button
              onClick={() => nav("/")}
              className="w-full h-12 bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold rounded-full gap-2"
            >
              {ar ? "العودة إلى الحاسبة" : "Back to Calculator"}
              <ArrowRight className={`h-4 w-4 ${ar ? "rotate-180" : ""}`} />
            </Button>
          </>
        ) : (
          <>
            <XCircle className="h-14 w-14 mx-auto text-red-400" />
            <h1 className={`text-2xl font-bold ${ar ? "font-arabic" : "font-outfit"}`}>
              {state.status === "cancelled" || result === "cancel"
                ? (ar ? "تم إلغاء الدفع" : "Payment cancelled")
                : ["declined", "failed"].includes(state.status)
                ? (ar ? "تم رفض البطاقة" : "Card declined")
                : (ar ? "تعذّر تأكيد الدفع" : "Could not confirm payment")}
            </h1>
            <p className="text-white/70 text-sm">
              {state.reason
                ? state.reason
                : ar
                ? "لم يُخصم أي مبلغ من حسابك. يمكنك المحاولة مجدداً."
                : "You have not been charged. Please try again."}
            </p>

            {/* Test-mode hint — helps users understand why real cards fail (only in test mode) */}
            {testMode && (
              <div className="rounded-xl p-3 border border-yellow-500/40 bg-yellow-950/40 text-yellow-100 text-start">
                <div className={`text-xs font-bold mb-1 ${ar ? "font-arabic" : "font-outfit"}`}>
                  {ar ? "🧪 نحن في وضع الاختبار — البطاقات الحقيقية لا تعمل" : "🧪 Test mode — real cards will not work"}
                </div>
                <div className="text-[11px] font-mono-num" dir="ltr">
                  <div><span className="opacity-70">Card:</span> <b>5123 4500 0000 0008</b></div>
                  <div><span className="opacity-70">Expiry:</span> <b>12/28</b> · <span className="opacity-70">CVV:</span> <b>100</b> · <span className="opacity-70">OTP:</span> <b>12345</b></div>
                </div>
              </div>
            )}

            <Button
              onClick={() => nav("/")}
              variant="outline"
              className="w-full h-12 border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c] rounded-full gap-2"
            >
              {ar ? "العودة إلى الحاسبة" : "Back to Calculator"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
